import { UIGameSpriteHandler } from '../../UIGameSpriteHandler.ts';
import { GameStateEnums } from '../../../../../shared/GameEnums.ts';
import { UIGameMapCornerView } from './UIGameMapCornerView.ts';
import { ArrayUtils } from '../../../../../shared/utils/ArrayUtils.ts';
import { AudioManager, AudioType } from '@colonist/audio';
import { ModeSettingEnum, VictoryPointStateReader } from '@colonist/game-base-types';
import { UIGameFloatingVictoryPointsController } from '../ui_game_floating_vp/UIGameFloatingVictoryPointsController.ts';
import { HiddenUIElement } from '@colonist/game-beginner-mode';
import { TileCornerBuildingType } from '@colonist/game-states';
import { isCornerBuildingKnight, Point } from '@colonist/game-mechanic-validators';
const UIGameEventEnums = GameStateEnums.UIGameEventEnums;
export class UIGameMapCornerController {
    onEvent(data, eventType) {
        switch(eventType){
            case UIGameEventEnums.MapStateUpdated:
                this.drawCorners(data == false);
                break;
            case UIGameEventEnums.BeginnerModeActionUpdated:
                this.onBeginnerModeActionUpdate(data);
                break;
            default:
                break;
        }
    }
    drawCorners(drawingExistingCorners = false) {
        for (const corner of this.uiGameManager.gameState.mapState.tileState.tileCorners){
            var _this_getCornerView;
            var _this_getCornerView_buildingType;
            const previousBuildingType = (_this_getCornerView_buildingType = (_this_getCornerView = this.getCornerView(corner.hexCorner)) === null || _this_getCornerView === void 0 ? void 0 : _this_getCornerView.buildingType) !== null && _this_getCornerView_buildingType !== void 0 ? _this_getCornerView_buildingType : undefined;
            this.removePreviouslyDrawnCorner(corner);
            if (corner.buildingType == undefined) continue;
            if (this.cornerExists(corner)) continue;
            if (this.isDrawingPredefinedInitialPlacements()) drawingExistingCorners = true;
            this.drawCorner(corner, previousBuildingType, drawingExistingCorners);
        }
    }
    static playSoundForCorner(newBuildingType, previousBuildingType) {
        switch(newBuildingType){
            case TileCornerBuildingType.Settlement:
                return AudioManager.play(previousBuildingType === TileCornerBuildingType.City ? AudioType.CityDestroy : AudioType.SettlementPlace);
            case TileCornerBuildingType.City:
                return AudioManager.play(AudioType.CityPlace);
            case TileCornerBuildingType.DestroyedCity:
                return AudioManager.play(AudioType.CityDestroy);
            default:
                return;
        }
    }
    cornerExists(corner) {
        for (const oldCornerView of this.buildingViews){
            if (oldCornerView.corner.sameCorner(corner)) return true;
        }
        return false;
    }
    removePreviouslyDrawnCorner(tileCorner) {
        const oldCornerView = this.getCornerView(tileCorner.hexCorner);
        if (oldCornerView == undefined) return;
        if (oldCornerView.buildingType == tileCorner.buildingType) return;
        oldCornerView.parent.removeChild(oldCornerView);
        ArrayUtils.removeElementFromArray(this.buildingViews, oldCornerView);
    }
    getCornerView(hexCorner) {
        for (const buildingView of this.buildingViews){
            if (buildingView.corner.hexCorner.sameCorner(hexCorner)) return buildingView;
        }
        return undefined;
    }
    drawCorner(corner, previousBuildingType, drawingExistingCorners) {
        const texture = UIGameSpriteHandler.getCornerImage(corner);
        if (texture == undefined) return;
        if (corner.buildingType == undefined) return;
        const mapView = this.uiGameManager.mapController.mapView;
        const center = corner.hexCorner.toPixel(mapView.mapCenter, mapView.hexagonHeight / 2);
        const buildingPoint = this.getPointForBuilding(corner.buildingType, center);
        if (buildingPoint == undefined) return;
        const animationStartY = drawingExistingCorners ? buildingPoint.y : buildingPoint.y - mapView.hexagonHeight;
        const buildingSize = this.getBuildingSize(corner.buildingType);
        const zIndex = isCornerBuildingKnight(corner.buildingType) ? mapView.layers.mapCornerKnightPieces : mapView.layers.mapCornerPieces;
        const building = new UIGameMapCornerView(corner, buildingPoint.x, animationStartY, buildingSize, buildingSize, zIndex, texture, mapView);
        const hexCorner = new Point(building.x, buildingPoint.y);
        building.animationController.animateTo(hexCorner);
        this.buildingViews.push(building);
        this.addBuildingAnimationCompleteCallback(building, corner, previousBuildingType, drawingExistingCorners);
        this.uiGameManager.mapController.mapView.sortChildren();
    }
    addBuildingAnimationCompleteCallback(building, corner, previousBuildingType, drawingExistingCorners) {
        if (drawingExistingCorners) return;
        if (corner.buildingType == previousBuildingType) return;
        building.animationController.animationCompleteCallBack = ()=>{
            if (corner.buildingType == undefined) return;
            UIGameMapCornerController.playSoundForCorner(corner.buildingType, previousBuildingType);
            this.floatingVPController.createFloatingVPForCorner(corner, previousBuildingType);
        };
    }
    getBuildingSize(cornerBuildingType) {
        switch(cornerBuildingType){
            case TileCornerBuildingType.Settlement:
            case TileCornerBuildingType.DestroyedCity:
            case TileCornerBuildingType.City:
                return this.uiGameManager.mapController.mapView.cornerCircleR * 2;
            case TileCornerBuildingType.KnightActiveLevel1:
            case TileCornerBuildingType.KnightActiveLevel2:
            case TileCornerBuildingType.KnightActiveLevel3:
            case TileCornerBuildingType.KnightInactiveLevel1:
            case TileCornerBuildingType.KnightInactiveLevel2:
            case TileCornerBuildingType.KnightInactiveLevel3:
                return this.uiGameManager.mapController.mapView.cornerCircleR * 1.6;
        }
    }
    getPointForBuilding(cornerBuildingType, cornerCenter) {
        switch(cornerBuildingType){
            case TileCornerBuildingType.Settlement:
            case TileCornerBuildingType.DestroyedCity:
            case TileCornerBuildingType.City:
                return this.getPointForCityAndSettlement(cornerCenter);
            case TileCornerBuildingType.KnightActiveLevel1:
            case TileCornerBuildingType.KnightActiveLevel2:
            case TileCornerBuildingType.KnightActiveLevel3:
            case TileCornerBuildingType.KnightInactiveLevel1:
            case TileCornerBuildingType.KnightInactiveLevel2:
            case TileCornerBuildingType.KnightInactiveLevel3:
                return this.getPointForKnight(cornerCenter);
        }
    }
    getPointForCityAndSettlement(cornerCenter) {
        const x = cornerCenter.x - this.uiGameManager.mapController.mapView.cornerCircleR;
        const y = cornerCenter.y - this.uiGameManager.mapController.mapView.cornerCircleR * 1.3;
        return new Point(x, y);
    }
    getPointForKnight(cornerCenter) {
        const knightRadius = this.uiGameManager.mapController.mapView.cornerCircleR * 1.6;
        const x = cornerCenter.x - knightRadius / 2;
        const y = cornerCenter.y - knightRadius / 2;
        return new Point(x, y);
    }
    isDrawingPredefinedInitialPlacements() {
        const modesWithPredefinedInitialPlacements = [
            ModeSettingEnum.BeginnerModeClassic4P
        ];
        const currentModeSetting = this.uiGameManager.gameState.gameModeData.modeSetting;
        if (!modesWithPredefinedInitialPlacements.includes(currentModeSetting)) return false;
        const currentTurnPlayer = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.currentState.currentTurnPlayerColor);
        const isPredefinedInitialPlacementForCurrentTurnPlayer = VictoryPointStateReader.totalPrivateVictoryPoints(currentTurnPlayer.victoryPointsState) <= 1;
        return isPredefinedInitialPlacementForCurrentTurnPlayer;
    }
    onBeginnerModeActionUpdate(data) {
        if (data.hiddenUIElements.includes(HiddenUIElement.HowToWinFloatingVP)) this.floatingVPController.hideHowToWinFloatingVPView();
    }
    constructor(uiGameManager){
        this.buildingViews = [];
        this.uiGameManager = uiGameManager;
        this.uiGameManager.eventController.registerUIGameMechanic(this);
        const drawingExistingCorners = true;
        this.drawCorners(drawingExistingCorners);
        this.floatingVPController = new UIGameFloatingVictoryPointsController(this.uiGameManager);
    }
}
